import { DeleteButton } from '@/components/Elements/Button'
import { InputCell, NumberInputCell, TooltipCell } from '@/components/Table/components/Cell'
import DatePickerCell from '@/components/Table/components/Cell/DatePickerCell'
import { FilterType } from '@/components/Table/components/Filter/TableFilter'
import { numberWithCommas } from '@/utils/stringHelper'
import { createColumnHelper } from '@tanstack/react-table'
import { useMemo } from 'react'
import { Link } from 'react-router-dom'

const columnHelper = createColumnHelper()

const useGCPRSubmissionColumns = ({
  canEditGCPRSubmission,
  canEditLogistic,
  showSubscriptionFields,
  handleDeleteRow,
}) => {
  return useMemo(
    () => [
      ...(canEditGCPRSubmission
        ? [
            columnHelper.display({
              id: 'actions',
              size: 40,
              cell: ({ row: { index } }) => <DeleteButton onClick={() => handleDeleteRow(index)} />,
            }),
          ]
        : []),
      columnHelper.display({
        id: 'vendorHistoryIndex',
        size: 10,
        header: () => <span>ref</span>,
        cell: ({ row: { index } }) => <TooltipCell text={index + 1} />,
      }),

      columnHelper.accessor('modelNumber', {
        size: 100,
        header: 'Model',
        cell: ({ getValue, row: { original } }) => {
          const modelNumber = getValue() || ''

          const mappedDetail = original?.detail

          if (mappedDetail && canEditLogistic) {
            return (
              <Link
                to={`/admin/GCMaterials/${mappedDetail?.id}`}
                target="_blank"
                style={{ color: 'blue', textDecoration: 'underline' }}
              >
                {modelNumber}
              </Link>
            )
          }

          if (canEditLogistic) {
            const createLink =
              `/admin/GCMaterials/create?` +
              `SEN=${modelNumber}&` +
              `articleType=${modelNumber}&` +
              `unitCost=${original?.unitCost ?? 0}&` +
              `description=${original?.description ?? ''}&` +
              `SAPMaterialNumber=${original?.SAPMaterialNumber ?? ''}&` +
              `license=${original?.license ?? ''}&` +
              `purchaseScenario=${original?.purchaseScenario ?? ''}&` +
              `currency=${original?.currency ?? ''}`

            return (
              <Link to={createLink} target="_blank" style={{ color: 'blue', textDecoration: 'underline' }}>
                {modelNumber}
              </Link>
            )
          }

          return <span>{modelNumber}</span>
        },
      }),
      columnHelper.accessor('description', {
        size: 120,
        header: 'Description',
      }),
      columnHelper.accessor('SAPMaterialNumber', {
        size: 100,
        header: 'SAP Material Number',
        cell: ({ getValue }) => {
          const value = getValue() === '' ? '(-)' : getValue()
          return (
            <TooltipCell
              color={value === '(-)' ? 'red' : 'black'}
              text={value}
              tooltip={value === '(-)' ? 'No mapped detail' : value}
            />
          )
        },
      }),
      columnHelper.accessor('quantity', {
        size: 100,
        header: 'Qty',
        cell: (cell) => <NumberInputCell {...cell} />,
      }),
      columnHelper.accessor('unit', {
        size: 100,
        header: 'unit',
        cell: ({ row: { original } }) => {
          const unit = original?.detail?.unit ? original.detail.unit : original?.unit
          return <TooltipCell text={`${unit ?? '(-)'}`} />
        },
      }),
      columnHelper.accessor('unitCost', {
        size: 100,
        header: 'Unit Cost ',
        cell: ({ row: { original } }) => {
          const unitCost = original?.detail?.unitCost ? parseFloat(original.detail.unitCost) : original?.unitCost
          const currency = original?.detail?.currency ? original.detail.currency : original?.currency

          return <TooltipCell text={`${currency ?? '$'}${numberWithCommas(unitCost)}`} />
        },
      }),
      columnHelper.accessor('totalPrice', {
        size: 100,
        header: 'Total Cost ',
        cell: ({ row: { original } }) => {
          const unitCost = original?.detail?.unitCost ? parseFloat(original.detail.unitCost) : original?.unitCost
          const quantity = original?.quantity ?? 0
          const totalPrice = unitCost * quantity
          const currency = original?.detail?.currency ? original.detail.currency : original?.currency
          return <TooltipCell text={`${currency ?? '$'} ${numberWithCommas(totalPrice)}`} />
        },
      }),

      columnHelper.accessor('purchaseScenario', {
        size: 100,
        header: 'Purchase Scenario',
        cell: (cell) => <InputCell {...cell} extraDisable={!canEditGCPRSubmission} />,
      }),
      columnHelper.accessor('subSystemName', {
        size: 100,
        header: 'Sub System',
      }),
      columnHelper.accessor('vendor', {
        size: 100,
        header: 'Vendor',
      }),
      columnHelper.display({
        id: 'MOQ',
        size: 100,
        header: 'MOQ',
        cell: ({ row: { original } }) => {
          const moq = original?.detail?.MOQ ?? '(-)'
          return <TooltipCell text={moq} />
        },
      }),
      columnHelper.accessor('PONumber', {
        size: 150,
        header: 'PO#',
        cell: (cell) => <InputCell {...cell} extraDisable={!canEditGCPRSubmission} />,
      }),
      columnHelper.display({
        id: 'CSID',
        size: 100,
        header: 'CSID',
        accessorFn: (row) => (row?.detail?.CSID == null ? '(-)' : row.detail.CSID ? 'Yes' : 'No'),
        enableColumnFilter: true,
        meta: {
          filterVariant: FilterType.SELECT,
          filterOptions: [
            { label: '(-)', value: '(-)' },
            { label: 'Yes', value: 'Yes' },
            { label: 'No', value: 'No' },
          ],
        },
        cell: ({ getValue }) => <TooltipCell text={getValue()} />,
      }),
      columnHelper.display({
        id: 'zuroa',
        size: 100,
        header: 'ZUROA',
        accessorFn: (row) => (row?.detail?.zuroa == null ? '(-)' : row.detail.zuroa ? 'Yes' : 'No'),
        enableColumnFilter: true,
        meta: {
          filterVariant: FilterType.SELECT,
          filterOptions: [
            { label: '(-)', value: '(-)' },
            { label: 'Yes', value: 'Yes' },
            { label: 'No', value: 'No' },
          ],
        },
        cell: ({ getValue }) => <TooltipCell text={getValue()} />,
      }),
      columnHelper.display({
        id: 'isBuildingX',
        size: 100,
        header: 'Building X',
        accessorFn: (row) => (row?.detail?.isBuildingX == null ? '(-)' : row.detail.isBuildingX ? 'Yes' : 'No'),
        enableColumnFilter: true,
        meta: {
          filterVariant: FilterType.SELECT,
          filterOptions: [
            { label: '(-)', value: '(-)' },
            { label: 'Yes', value: 'Yes' },
            { label: 'No', value: 'No' },
          ],
        },
        cell: ({ getValue }) => <TooltipCell text={getValue()} />,
      }),
      columnHelper.display({
        id: 'isStandAlonePR',
        size: 100,
        header: 'Is Standalone PR',
        accessorFn: (row) => (row?.detail?.isStandAlonePR == null ? '(-)' : row.detail.isStandAlonePR ? 'Yes' : 'No'),
        enableColumnFilter: true,
        meta: {
          filterVariant: FilterType.SELECT,
          filterOptions: [
            { label: '(-)', value: '(-)' },
            { label: 'Yes', value: 'Yes' },
            { label: 'No', value: 'No' },
          ],
        },
        cell: ({ getValue }) => <TooltipCell text={getValue()} />,
      }),
      columnHelper.display({
        id: 'discontinued',
        size: 100,
        header: 'Discontinued',
        accessorFn: (row) => (row?.detail?.discontinued == null ? '(-)' : row.detail.discontinued ? 'Yes' : 'No'),
        enableColumnFilter: true,
        meta: {
          filterVariant: FilterType.SELECT,
          filterOptions: [
            { label: '(-)', value: '(-)' },
            { label: 'Yes', value: 'Yes' },
            { label: 'No', value: 'No' },
          ],
        },
        cell: ({ getValue }) => <TooltipCell text={getValue()} />,
      }),
      columnHelper.display({
        id: 'chargeType',
        size: 100,
        header: 'Charge Type',
        accessorFn: (row) => row?.detail?.chargeType || '(-)',
        enableColumnFilter: true,
        cell: ({ getValue }) => <TooltipCell text={getValue()} />,
      }),
      columnHelper.display({
        id: 'priceTerm',
        size: 100,
        header: 'Price Term',
        accessorFn: (row) => row?.detail?.priceTerm || '(-)',
        enableColumnFilter: true,
        cell: ({ getValue }) => <TooltipCell text={getValue()} />,
      }),

      ...(showSubscriptionFields
        ? [
            columnHelper.accessor('subscriptionId', {
              size: 120,
              header: 'Subscription ID',
              cell: (cell) => <InputCell {...cell} />,
            }),
            columnHelper.accessor('startDate', {
              size: 150,
              header: 'Start date',
              cell: (cell) => <DatePickerCell {...cell} />,
            }),

            columnHelper.accessor('trialEnd', {
              size: 150,
              header: 'Trial End',
              cell: (cell) => <DatePickerCell {...cell} />,
            }),
            columnHelper.accessor('contractEnd', {
              size: 150,
              header: 'Contract End',
              cell: (cell) => <DatePickerCell {...cell} />,
            }),
          ]
        : []),
    ],
    [canEditGCPRSubmission, canEditLogistic, showSubscriptionFields, handleDeleteRow]
  )
}

export default useGCPRSubmissionColumns
